'use client'

import { createContext, useContext, useEffect, useState } from 'react'

type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  resolvedTheme: ResolvedTheme
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

const MEDIA_QUERY = '(prefers-color-scheme: dark)'
// Key from the old manual toggle. A value left there must not override the OS.
const LEGACY_STORAGE_KEY = 'theme'

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light'
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(resolved)
  root.style.colorScheme = resolved
}

// Theme policy: the operating system decides. Dark only when the OS asks for dark;
// light otherwise, including when the OS gives no preference. There is no in-app override.
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Server and first client render both use 'light' to avoid a hydration mismatch.
  // themeInitScript has already set the real class on <html> before hydration.
  const [resolved, setResolved] = useState<ResolvedTheme>('light')

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY)
    } catch { /* storage unavailable */ }

    const sync = () => {
      const r = getSystemTheme()
      setResolved(r)
      applyTheme(r)
    }
    sync()

    const mq = window.matchMedia(MEDIA_QUERY)
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  return <ThemeContext.Provider value={{ resolvedTheme: resolved }}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) return { resolvedTheme: 'light' }
  return ctx
}

/**
 * Static inline script string for FOUC prevention.
 * Written as a literal string (NOT Function.toString()) so build tools
 * can't inject artifacts like __name() into it.
 */
export const themeInitScript = `(function(){try{var r=window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";document.documentElement.classList.add(r);document.documentElement.style.colorScheme=r}catch(e){document.documentElement.classList.add("light")}})()`;
